import { notifications } from '@mantine/notifications'

export interface OnboardingError {
  message: string
  type: 'network' | 'validation' | 'payment' | 'unknown'
  retryable: boolean
}

/**
 * Standardizes error handling for onboarding flow
 */
export function handleOnboardingError(error: any, context: string): OnboardingError {
  let message: string
  let type: OnboardingError['type'] = 'unknown'
  let retryable = true

  // FastAPI errors come under `detail` (string for HTTPException, array for 422);
  // other endpoints use `message`. Only surface a string detail.
  const detail = error?.response?.data?.detail
  const detailMessage = typeof detail === 'string' ? detail : undefined
  if (error?.response?.data?.message || detailMessage) {
    message = error.response.data.message ?? detailMessage
    type = 'validation'
  } else if (error?.message?.includes('network') || error?.code === 'NETWORK_ERROR') {
    message = 'Network error. Please check your connection and try again.'
    type = 'network'
  } else if (error?.message?.includes('payment') || error?.type === 'card_error') {
    message = error.message || 'Payment failed. Please check your payment details.'
    type = 'payment'
  } else if (error?.message) {
    message = error.message
  } else {
    message = `An unexpected error occurred during ${context}. Please try again.`
    retryable = true
  }

  return { message, type, retryable }
}

/**
 * Shows a standardized error notification for onboarding errors
 */
export function showOnboardingErrorNotification(error: OnboardingError, _context: string) {
  notifications.show({
    title: getErrorTitle(error.type),
    message: error.message,
    color: 'red',
    autoClose: error.type === 'network' ? false : 5000, // Keep network errors visible longer
  })
}

function getErrorTitle(type: OnboardingError['type']): string {
  switch (type) {
    case 'network':
      return 'Connection Error'
    case 'validation':
      return 'Validation Error'
    case 'payment':
      return 'Payment Error'
    default:
      return 'Error'
  }
}

/**
 * Gets user-friendly error message for common onboarding scenarios
 */
export function getOnboardingErrorMessage(context: string, defaultMessage?: string): string {
  const messages: Record<string, string> = {
    'create-account': 'Failed to create account. Please check your information and try again.',
    'verify-email': 'Failed to verify email. Please check your verification code.',
    'resend-code': 'Failed to resend verification code. Please try again in a moment.',
    'load-plans': 'Failed to load available plans. Please refresh the page.',
    'generate-payment': 'Failed to set up payment. Please try again.',
    'create-customer': 'Failed to complete account setup. Please contact support if this continues.',
  }

  return messages[context] || defaultMessage || `An error occurred during ${context}.`
}
